import React, { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useL } from "@/lib/useL";

export const q = (tid) => document.querySelector(`[data-testid="${tid}"]`);

export const MAIN_STEPS = [
  { el: () => q("tab-text")?.parentElement, fr: ["1 · Choisissez la source", "Texte collé, fichier (PDF, Word, photo), lien web, photo de livre ou dossier de plusieurs documents. Pour commencer, restez sur « Coller un texte »."], en: ["1 · Choose the source", "Pasted text, file (PDF, Word, photo), web link, book photo or a folder of several documents. To start, stay on « Paste text »."] },
  { el: () => q("paste-text-input"), fr: ["2 · Collez votre texte", "Copiez un article, un cours ou un chapitre, puis collez-le ici (Ctrl+V ou appui long sur téléphone)."], en: ["2 · Paste your text", "Copy an article, a lesson or a chapter, then paste it here (Ctrl+V or long press on a phone)."] },
  { el: () => q("format-select") || q("config-more-options"), fr: ["3 · Réglez le résumé", "Choisissez le format (classique, fiche de révision, DYS…), la longueur et le niveau de classe. Les bulles « i » expliquent chaque choix."], en: ["3 · Set up the summary", "Choose the format (classic, revision sheet, DYS…), the length and the class level. The « i » bubbles explain each option."] },
  { el: () => (q("summarize-submit-button")?.offsetParent ? q("summarize-submit-button") : q("summarize-btn-mobile")), fr: ["4 · Lancez le résumé", "Cliquez ici. Le coût en crédits est affiché juste au-dessus avant de lancer. Le résumé apparaît en quelques secondes."], en: ["4 · Start the summary", "Click here. The credit cost is shown just above before you start. The summary appears in a few seconds."] },
];

const PAD = 8;

export function GuidedTour({ open, onClose, onShowImage, steps: STEPS = MAIN_STEPS, onStart = () => q("tab-text")?.click() }) {
  const L = useL();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const r = STEPS[i]?.el()?.getBoundingClientRect();
    if (!r || (!r.width && !r.height)) return setRect(null);
    const left = Math.max(2, r.left - PAD);
    const top = Math.max(2, r.top - PAD);
    setRect({ top, left, width: Math.min(window.innerWidth - 4 - left, r.right + PAD - left), height: r.bottom + PAD - top });
  }, [i, STEPS]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) onStart(); else setI(0); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    STEPS[i]?.el()?.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(measure, 450);
    const t2 = setTimeout(measure, 900);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); clearTimeout(t2); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [open, i, measure, STEPS]);

  if (!open || !STEPS[i]) return null;
  const [title, body] = L(STEPS[i].fr, STEPS[i].en);
  const last = i === STEPS.length - 1;
  const below = rect && rect.top + rect.height + 230 < window.innerHeight;
  const cardTop = rect ? (below ? rect.top + rect.height + 12 : Math.max(12, rect.top - 222)) : window.innerHeight / 2 - 100;
  const cardLeft = rect ? Math.min(Math.max(12, rect.left), window.innerWidth - 352) : window.innerWidth / 2 - 170;
  const btn = "font-mono uppercase tracking-[0.16em] text-[11px] px-4 py-2 transition-colors";

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
      <div className="absolute inset-0" onClick={onClose} />
      {rect && (
        <div className="absolute pointer-events-none border-2 border-[#FF2A00] transition-all duration-300"
          style={{ ...rect, boxShadow: "0 0 0 9999px rgba(10,10,20,0.6)" }} data-testid="guided-tour-spotlight" />
      )}
      <div role="dialog" aria-label={title} className="absolute w-[min(340px,calc(100vw-24px))] bg-card border border-border shadow-xl p-5 transition-all duration-300"
        style={{ top: cardTop, left: cardLeft }} data-testid="guided-tour-card">
        <button type="button" onClick={onClose} aria-label={L("Fermer", "Close")} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" data-testid="guided-tour-close"><X className="w-4 h-4" /></button>
        <div className="font-bold text-[15px] pr-6 mb-2" data-testid="guided-tour-title">{title}</div>
        <p className="text-sm text-foreground/80 leading-relaxed mb-4" data-testid="guided-tour-body">{body}</p>
        {last && onShowImage && (
          <button type="button" onClick={() => { onClose(); onShowImage(); }} className="text-[13px] underline text-[#002FA7] mb-4 block" data-testid="guided-tour-image-link">
            {L("Voir l'image fléchée de toute l'interface", "See the annotated picture of the whole interface")}
          </button>
        )}
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="guided-tour-progress">{i + 1} / {STEPS.length}</span>
          <div className="flex gap-2">
            {i > 0 && <button type="button" onClick={() => setI(i - 1)} className={`${btn} border border-border hover:border-[#002FA7]`} data-testid="guided-tour-prev">{L("Retour", "Back")}</button>}
            <button type="button" onClick={() => (last ? onClose() : setI(i + 1))} className={`${btn} bg-[#002FA7] text-white hover:bg-[#00227A]`} data-testid="guided-tour-next">
              {last ? L("Terminer", "Finish") : L("Suivant", "Next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuidedTour;
